import React from "react";
import { 
  UserCircleIcon, 
  GlobeAltIcon, 
  ClipboardDocumentListIcon, 
  ChatBubbleLeftEllipsisIcon, 
  CheckBadgeIcon, 
  XCircleIcon, 
  ClockIcon,
  ArchiveBoxIcon 
} from "@heroicons/react/24/solid";
import { toggleArchivePartenaire } from "./partenairesAPI";
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from "@headlessui/react";


export default function PartenaireDetail({ partenaire, onClose, onEdit, refreshData }) {
  if (!partenaire) return null;

  // 🎨 Définition des couleurs et icônes en fonction du statut de conformité
  const statutConfig = {
    "Actif": { icon: <CheckBadgeIcon className="h-6 w-6 text-green-500" />, bgColor: "bg-green-100 text-green-800" },
    "Inactif": { icon: <XCircleIcon className="h-6 w-6 text-red-500" />, bgColor: "bg-red-100 text-red-800" },
    "Onboarding": { icon: <ClockIcon className="h-6 w-6 text-orange-500" />, bgColor: "bg-orange-100 text-orange-800" },
    "Non renseigné": { icon: <ClockIcon className="h-6 w-6 text-gray-500" />, bgColor: "bg-gray-100 text-gray-800" }
  };

  const statut = partenaire.statutConformite || "Non renseigné";
  const { icon, bgColor } = statutConfig[statut] || statutConfig["Non renseigné"];

  const handleArchive = async () => {
    await toggleArchivePartenaire(partenaire._id, partenaire.archived);
    refreshData(); // 🔄 Rafraîchir la liste après l'archivage/désarchivage
    onClose(); // Fermer la modale après action
  };

  return (
    <Dialog open={!!partenaire} onClose={onClose} className="relative z-[999]">
      <DialogBackdrop className="fixed inset-0 bg-black/50" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <DialogPanel className="relative bg-white p-6 rounded-lg w-[500px] max-h-[90vh] overflow-y-auto shadow-xl border border-gray-200">
          <DialogTitle className="text-2xl font-bold text-center mb-4"> 
        
          {/* En-tête avec le logo */}
          <div className="flex flex-col items-center">
            {partenaire.logo ? (
              <img
                src={partenaire.logo}
                alt="Logo"
                className="h-20 w-20 object-contain rounded-lg shadow-md mb-3"
              />
            ) : (
              <UserCircleIcon className="h-20 w-20 text-gray-400 mb-3" /> 
            )}
            <h2 className="text-2xl font-bold text-gray-900">{partenaire.nom_societe}</h2>
            <p className="text-sm text-gray-500">Ajouté le : {new Date(partenaire.dateAjout).toLocaleDateString("fr-FR")}</p>
          </div>
          </DialogTitle>

          {/* Détails du partenaire */}
          <div className="mt-5 space-y-4">
            {/* Informations principales */}
            <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800">Informations principales</h3>
              <p className="text-sm text-gray-600">Type de structure : <span className="font-medium text-gray-800">{partenaire.type_structure || "Non renseigné"}</span></p>
              <p className="text-sm text-gray-600">Classe d’actifs : <span className="font-medium text-gray-800">{partenaire.classe_actifs || "Non renseigné"}</span></p>
              <p className="text-sm text-gray-600">Numéro d'enregistrement : <span className="font-medium text-gray-800">{partenaire.numeroEnregistrement || "Non renseigné"}</span></p>
            </div>

            {/* Contact */}
            <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800">Contact</h3>
              <p className="text-sm text-gray-600">Email : <span className="font-medium text-gray-800">{partenaire.email || "Non renseigné"}</span></p>
              <p className="text-sm text-gray-600">Téléphone : <span className="font-medium text-gray-800">{partenaire.telephone || "Non renseigné"}</span></p>
              <p className="text-sm text-gray-600">Adresse : <span className="font-medium text-gray-800">{partenaire.adresse || "Non renseigné"}</span></p>
              <p className="text-sm text-gray-600">Référent : <span className="font-medium text-gray-800">{partenaire.nom_referent || "Non renseigné"}</span></p>
            </div>

            {/* Statut et Volume d'affaires */}
            <div className="bg-gray-50 p-4 rounded-lg shadow-sm">
              <h3 className="text-lg font-semibold text-gray-800">Statut & Finances</h3>
              <p className="text-sm text-gray-600">Statut du partenariat : <span className="font-medium text-gray-800">{partenaire.statutConformite || "Non renseigné"}</span></p>
              <p className="text-sm text-gray-600">Volume d'affaires : <span className="font-medium text-gray-800">{partenaire.volumeAffaires ? `${partenaire.volumeAffaires} €` : "0 €"}</span></p>
            </div>

            {/* Site web */}
            {partenaire.siteWeb && (
              <div className="bg-gray-50 p-4 rounded-lg shadow-sm flex items-center space-x-2">
                <GlobeAltIcon className="h-5 w-5 text-gray-600" />
                <a 
                  href={partenaire.siteWeb} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="text-blue-600 font-medium hover:underline"
                >
                  Voir le site Web
                </a>
              </div>
            )}

            {/* Description */}
            {partenaire.description && (
              <div className="bg-gray-50 p-4 rounded-lg shadow-sm flex items-start space-x-2">
                <ClipboardDocumentListIcon className="h-5 w-5 text-gray-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Description</h3>
                  <p className="text-sm text-gray-800">{partenaire.description}</p>
                </div>
              </div>
            )}

            {/* Documents requis */}
            {partenaire.documentsRequis && partenaire.documentsRequis.length > 0 && (
              <div className="bg-gray-50 p-4 rounded-lg shadow-sm flex items-start space-x-2">
                <ClipboardDocumentListIcon className="h-5 w-5 text-gray-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Documents requis</h3>
                  <ul className="text-sm text-gray-800 list-disc pl-5">
                    {partenaire.documentsRequis.map((doc, index) => (
                      <li key={index}>{doc}</li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Commentaires internes */}
            {partenaire.commentairesInternes && (
              <div className="bg-gray-50 p-4 rounded-lg shadow-sm flex items-start space-x-2">
                <ChatBubbleLeftEllipsisIcon className="h-5 w-5 text-gray-600" />
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Commentaires internes</h3>
                  <p className="text-sm text-gray-800">{partenaire.commentairesInternes}</p>
                </div>
              </div>
            )}
          </div>

          {/* 📌 Statut de conformité en bas */}
          <div className={`mt-6 flex items-center justify-center p-3 rounded-lg shadow-sm ${bgColor}`}>
            {icon}
            <span className="ml-2 text-lg font-semibold">Statut : {statut}</span>
          </div>

          {/* Boutons Modifier et Fermer */}
          <div className="flex justify-between mt-6">
            <button 
              type="button"
              className="bg-blue-500 text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition"
              onClick={() => {
                onEdit(partenaire);
              }}
            >
              Modifier
            </button>

            <button 
              type="button"
              className="bg-gray-700 text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition flex items-center"
              onClick={handleArchive}
            >
              <ArchiveBoxIcon className="h-5 w-5 mr-2" />
              {partenaire.archived ? "Désarchiver" : "Archiver"}
            </button>

            <button 
              type="button"
              className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600 transition"
              onClick={onClose}
            >
              Fermer
            </button>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
